import { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DateTime, Interval } from 'luxon';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import ShopSelector from '@/components/selectors/ShopSelector';
import ModuleSelector from '@/components/selectors/ModuleSelector';
import IntervalSelector from '@/components/selectors/IntervalSelector';
import DateSelector from '@/components/selectors/DateSelector';
import TimeSelector from '@/components/selectors/TimeSelector';
import type { ModuleType } from './types';
import { useSelectedModule } from '@core/hooks/useSelectedModule';
import { useStore } from '@core/store';
import { shallow } from 'zustand/shallow';
import { useShopPersistence } from '@core/hooks/usePersistence';
import { FilterRef } from '@features/statistics/types.ts';

export interface DayDetailData {
    selectedShopId: string;
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
    interval: number;
}

export interface FilterDayDetailProps {
    forType: 'trolleyShelter' | 'TrolleyShelter' | 'zoneTracker' | 'ZoneTracker' | 'lineTracker' | 'LineTracker';
    intervals?: number[];
    onLoadData: (data: DayDetailData) => void;
    ref?: Ref<FilterRef>;
}

const FilterDayDetail = ({
    forType: forProp,
    intervals = [60, 30, 15, 10, 5],
    onLoadData,
    ref
}: FilterDayDetailProps) => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [moduleId, setModuleId] = useState<string | null>(null);
    const [day, setDay] = useState(DateTime.local().toISODate() ?? '');
    const [startTime, setStartTime] = useState('06:00');
    const [endTime, setEndTime] = useState('22:00');
    const [interval, setInterval] = useState(60);

    const hasInitialized = useRef(false);

    const { trolleyShelterIds, zoneTrackerIds, loadTrolleyShelterIds, loadZoneTrackerIds } = useStore(
        (state) => ({
            trolleyShelterIds: state.trolleyShelterIds,
            zoneTrackerIds: state.zoneTrackerIds,
            loadTrolleyShelterIds: state.loadTrolleyShelterIds,
            loadZoneTrackerIds: state.loadZoneTrackerIds
        }),
        shallow
    );

    useEffect(() => {
        const loadModuleData = async () => {
            const normalizedType = forProp.toLowerCase();
            if (normalizedType.includes('trolleyshelter') && (!trolleyShelterIds || trolleyShelterIds.length === 0)) {
                await loadTrolleyShelterIds();
            } else if (normalizedType.includes('zonetracker') && (!zoneTrackerIds || zoneTrackerIds.length === 0)) {
                await loadZoneTrackerIds();
            }
        };
        loadModuleData();
    }, [forProp, trolleyShelterIds, zoneTrackerIds, loadTrolleyShelterIds, loadZoneTrackerIds]);

    const filteredModuleIds = useMemo(() => {
        if (!selectedShopIds[0]) return [];
        const normalizedType = forProp.toLowerCase();
        let moduleList: any[] = [];
        if (normalizedType.includes('trolleyshelter')) moduleList = trolleyShelterIds;
        else if (normalizedType.includes('zonetracker')) moduleList = zoneTrackerIds;
        return moduleList.filter(
            (module) => module.shopId === selectedShopIds[0] && !module.disabled && !module.deleteDate
        );
    }, [selectedShopIds, forProp, trolleyShelterIds, zoneTrackerIds]);

    const selectedModule = useSelectedModule({
        moduleId,
        moduleType: forProp,
        trolleyShelterIds: filteredModuleIds,
        zoneTrackerIds: filteredModuleIds
    });

    const availableIntervals = useMemo(() => {
        const startTimeObj = DateTime.fromISO(startTime, { zone: 'UTC' });
        const endTimeObj = DateTime.fromISO(endTime, { zone: 'UTC' });
        const diffHours = Interval.fromDateTimes(startTimeObj, endTimeObj).length('hours');
        if (diffHours <= 1) return intervals;
        else if (diffHours > 1 && diffHours <= 3) return intervals.filter((i) => i >= 15);
        return intervals.filter((i) => i >= 30);
    }, [startTime, endTime, intervals]);

    const handleModuleChange = useCallback((selectedModuleId: string | null) => {
        setModuleId(selectedModuleId);
    }, []);

    const handleIntervalChange = useCallback((newInterval: number) => {
        setInterval(newInterval);
    }, []);

    useEffect(() => {
        const selectedModuleId = selectedModule?.id ?? null;
        if (selectedModuleId !== moduleId) return;
        emitLoadData();
    }, [selectedModule, moduleId, selectedShopIds, day, startTime, endTime, interval]);

    const emitLoadData = useCallback(() => {
        if (!selectedModule || !selectedShopIds[0]) return;
        const dayObj = DateTime.fromISO(day, { zone: 'UTC' });
        const startTimeObj = DateTime.fromISO(startTime, { zone: 'UTC' });
        const endTimeObj = DateTime.fromISO(endTime, { zone: 'UTC' });
        const start = dayObj.set({ hour: startTimeObj.hour, minute: startTimeObj.minute }).startOf('minute').toISO();
        const end = dayObj
            .set({ hour: endTimeObj.hour, minute: endTimeObj.minute - 1 })
            .endOf('minute')
            .toISO();
        if (start && end) {
            onLoadData({
                selectedShopId: selectedShopIds[0],
                deviceId: selectedModule.deviceId,
                moduleId: selectedModule.id,
                start,
                end,
                interval
            });
        }
    }, [selectedModule, selectedShopIds, day, startTime, endTime, interval]);

    useEffect(() => {
        if (hasInitialized.current) return;
        const shopIdParam = searchParams.get('selectedShopId');
        const moduleIdParam = searchParams.get('moduleId');
        if (shopIdParam) setSelectedShopIds([shopIdParam]);
        if (moduleIdParam) setModuleId(moduleIdParam);
        hasInitialized.current = true;
    }, []);

    useImperativeHandle(ref, () => ({ refresh: () => emitLoadData() }), [emitLoadData]);

    const today = DateTime.local();

    // Compute maxTime for start time (must be before end time)
    const startTimeMax = useMemo(() => {
        const parts = endTime.split(':');
        if (parts.length < 2) return undefined;
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        return DateTime.local().set({ hour: h, minute: m }).minus({ minutes: 5 });
    }, [endTime]);

    // Compute minTime for end time (must be after start time)
    const endTimeMin = useMemo(() => {
        const parts = startTime.split(':');
        if (parts.length < 2) return undefined;
        const h = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10);
        return DateTime.local().set({ hour: h, minute: m }).plus({ minutes: 5 });
    }, [startTime]);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 3.5,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    mb: 5,
                    mt: 1
                }}
            >
                <Box sx={{ flex: '0 0 auto' }}>
                    <ShopSelector
                        value={selectedShopIds[0] ?? null}
                        onChange={(id) => {
                            setSelectedShopIds(id ? [id] : []);
                            setModuleId(null);
                        }}
                        label={t('shop')}
                    />
                </Box>

                <Box sx={{ width: 250, flex: '0 0 auto' }}>
                    <ModuleSelector
                        value={moduleId}
                        onChange={handleModuleChange}
                        shopId={selectedShopIds[0] ?? null}
                        moduleType={forProp as ModuleType}
                        label={t(forProp)}
                        modules={filteredModuleIds}
                        autoSelectFirst={true}
                    />
                </Box>

                <Box sx={{ width: 240, flex: '0 0 auto' }}>
                    <DateSelector value={day || null} onChange={setDay} label={t('day')} maxDate={today} />
                </Box>

                <Box sx={{ flex: '0 0 auto' }}>
                    <TimeSelector
                        value={startTime}
                        onChange={setStartTime}
                        label={t('startTime')}
                        maxTime={startTimeMax}
                        minutesStep={5}
                    />
                </Box>

                <Box sx={{ flex: '0 0 auto' }}>
                    <TimeSelector
                        value={endTime}
                        onChange={setEndTime}
                        label={t('endTime')}
                        minTime={endTimeMin}
                        minutesStep={5}
                    />
                </Box>

                <Box sx={{ width: 150, flex: '0 0 auto' }}>
                    <IntervalSelector
                        value={interval}
                        onChange={handleIntervalChange}
                        intervals={availableIntervals}
                        unit="minutes"
                    />
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default FilterDayDetail;
